/**
 * SquaresGameProgress — iter87.599 (user-requested). Once a Squares
 * game has kicked off, this REPLACES the Schedule tile in the Cheer
 * section: live score + quarter-by-quarter sheet, the locked board
 * with owner initials, the live score's box + checkpoint winners
 * highlighted, hover-for-detail on any box, Total Pot, and a
 * score-likelihood heatmap tint (see squares_heatmap.py backend).
 * iter87.604 (user-reported) — iter87.603's team swatch + name
 * banners on the grid didn't fit on mobile; reverted back to the
 * plain yellow/home · red/away digit headers. Total Pot now lives
 * inside the scoreboard card (right above the grid) instead of its
 * own separate tile.
 * Read-only — the Squares modal (Draft section) is unchanged and
 * still owns picking/claiming.
 */
import { Fragment, useCallback, useEffect, useState } from "react";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { Switch } from "@/components/ui/switch";

const CHECKPOINT_LABELS = { q1: "Q1", q2: "Halftime", q3: "Q3", final: "Final" };
const PERIODS = ["q1", "q2", "q3", "q4", "ot"];

// iter87.606 (user-requested) — single-hue light-to-dark scale (lavender
// -> white) instead of the old multi-color tranche palette, so "more
// likely" reads as "darker", not just "a different color".
const TRANCHE_STYLE = {
    high: "bg-violet-600/50",
    moderate_high: "bg-violet-500/35",
    moderate: "bg-violet-400/22",
    moderate_low: "bg-violet-300/12",
    low: "bg-white/5",
};
const TRANCHE_LABEL = { high: "High", moderate_high: "Mod-High", moderate: "Moderate", moderate_low: "Mod-Low", low: "Low" };

export default function SquaresGameProgress({ tournamentId, meId }) {
    const [data, setData] = useState(null);
    const [loading, setLoading] = useState(true);
    const [loadError, setLoadError] = useState(false);
    const [hoverIdx, setHoverIdx] = useState(null);
    // iter87.606 (user-requested) — heatmap tint starts OFF; the board
    // reads as the plain locked board until the player opts in.
    const [heatmapOn, setHeatmapOn] = useState(false);

    const load = useCallback(async () => {
        try {
            const d = await api.squaresGetCheckpoints(tournamentId);
            setData(d);
            setLoadError(false);
        } catch (e) {
            setLoadError(true);
        }
        setLoading(false);
    }, [tournamentId]);

    useEffect(() => {
        load();
        const id = setInterval(load, 20000);
        return () => clearInterval(id);
    }, [load]);

    if (loading && !data) {
        return <div className="text-center text-zinc-500 text-sm py-10" data-testid="game-progress-loading">Loading live game…</div>;
    }
    if (loadError && !data) {
        return <div className="text-center text-blaze text-sm py-10" data-testid="game-progress-error">Couldn't load the live game right now — try again shortly.</div>;
    }
    if (!data?.locked) {
        return <div className="text-center text-zinc-500 text-sm py-10" data-testid="game-progress-not-locked">The board hasn't locked yet — check back once it has.</div>;
    }

    const ls = data.live_score;
    const byIdx = {};
    (data.grid || []).forEach((b) => { byIdx[b.box_index] = b; });
    const winnersByIdx = {};
    Object.entries(data.checkpoints || {}).forEach(([cp, r]) => {
        if (r.ready && r.box_index != null) (winnersByIdx[r.box_index] = winnersByIdx[r.box_index] || []).push(cp);
    });
    const hoverBox = hoverIdx != null ? byIdx[hoverIdx] : null;

    return (
        <div className="space-y-4" data-testid="squares-game-progress">
            <div className="rounded-xl border border-white/10 bg-ink p-4" data-testid="game-progress-scoreboard">
                <div className="flex items-center justify-between gap-2">
                    <div className="text-center flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-cyber mb-1 truncate">{ls?.home_name || "Home"}</div>
                        <div className="text-3xl font-black text-white" data-testid="game-progress-home-score">{ls?.home_score ?? "–"}</div>
                    </div>
                    <div className="text-center shrink-0 px-2">
                        <div className={`text-[10px] font-bold uppercase tracking-wide px-2 py-0.5 rounded-full inline-flex items-center gap-1 ${ls?.finished ? "bg-amber-400/20 text-amber-300" : "bg-rose-500/20 text-rose-300"}`} data-testid="game-progress-status">
                            {!ls?.finished && <span className="w-1.5 h-1.5 rounded-full bg-rose-400 animate-pulse" />}
                            {ls?.finished ? "FINAL" : (ls?.live_detail || ls?.status_short || "LIVE")}
                        </div>
                    </div>
                    <div className="text-center flex-1 min-w-0">
                        <div className="text-[10px] uppercase tracking-wide text-heat mb-1 truncate">{ls?.away_name || "Away"}</div>
                        <div className="text-3xl font-black text-white" data-testid="game-progress-away-score">{ls?.away_score ?? "–"}</div>
                    </div>
                </div>
                {ls && (
                    <table className="w-full mt-4 text-[11px]" data-testid="game-progress-quarter-sheet">
                        <thead>
                            <tr className="text-zinc-500 uppercase text-[9px]">
                                <th className="text-left font-normal"> </th>
                                {PERIODS.map((p) => (<th key={p} className="font-normal">{p.toUpperCase()}</th>))}
                                <th className="text-volt">T</th>
                            </tr>
                        </thead>
                        <tbody>
                            <tr className="border-t border-white/10">
                                <td className="py-1.5 text-cyber font-bold truncate max-w-[90px]">{ls.home_name}</td>
                                {PERIODS.map((p) => (<td key={p} className="text-center text-zinc-300">{ls.home_period_points?.[p] ?? "–"}</td>))}
                                <td className="text-center font-bold text-volt">{ls.home_score}</td>
                            </tr>
                            <tr className="border-t border-white/5">
                                <td className="py-1.5 text-heat font-bold truncate max-w-[90px]">{ls.away_name}</td>
                                {PERIODS.map((p) => (<td key={p} className="text-center text-zinc-300">{ls.away_period_points?.[p] ?? "–"}</td>))}
                                <td className="text-center font-bold text-volt">{ls.away_score}</td>
                            </tr>
                        </tbody>
                    </table>
                )}
                {!ls && (
                    <p className="text-center text-xs text-zinc-500 mt-3" data-testid="game-progress-no-live">
                        Live score unavailable right now — checking back shortly.
                    </p>
                )}
                {/* iter87.604 (user-requested) — Total Pot used to be its
                    own tile between the scoreboard and the grid; moved
                    into the scoreboard card itself so it's the first
                    thing seen, right above the board. */}
                {data.live_pot && (
                    <div className="mt-3 pt-3 border-t border-white/10 flex items-center justify-between gap-3" data-testid="game-progress-pot">
                        <div className="text-[10px] uppercase tracking-wide text-zinc-500">Total Pot {data.full_locked ? "(final)" : "(live)"}</div>
                        <div className="text-right">
                            <div className="font-bold text-volt" data-testid="game-progress-pot-amount">{data.live_pot.net_pot} tokens</div>
                            <div className="text-[10px] text-zinc-500">{data.live_pot.n_confirmed} confirmed</div>
                        </div>
                    </div>
                )}
            </div>

            <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="game-progress-grid-card">
                <div className="flex items-center justify-end gap-2 mb-2 min-h-[28px]">
                    {hoverBox ? (
                        <div className="text-[11px] font-bold text-white text-right" data-testid="game-progress-hover-detail">
                            {ls?.home_name || "Home"} {hoverBox.home_digit} · {ls?.away_name || "Away"} {hoverBox.away_digit}
                            {hoverBox.username ? ` — ${hoverBox.username}${hoverBox.participant_id === meId ? " (You!)" : ""}` : " — unclaimed"}
                        </div>
                    ) : (
                        <div className="text-[10px] text-zinc-600">Hover or tap a box for detail</div>
                    )}
                </div>
                {/* iter87.605 (user-requested) — box-border legend
                    moved above the grid so it reads as a "key" before
                    the board, not an afterthought below it. */}
                <div className="flex flex-wrap items-center justify-center gap-x-3 gap-y-1 text-[9px] uppercase tracking-wide text-zinc-500 mb-2">
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm ring-2 ring-white" /> Live score</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-cyan-400" /> Checkpoint win</span>
                    <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-sm border-2 border-amber-300" /> Final win</span>
                </div>
                {/* iter87.607 (user-requested) — the grid used to be a
                    small fixed-pixel `inline-grid` centered with empty
                    space on both sides; now a fluid `w-full` grid with
                    `1fr` box columns so it actually fills the tile's
                    width (the behavior the Squares Selection modal's
                    pick grid already had), with `aspect-square` boxes
                    so they stay square at any width. */}
                <div className="w-full">
                    <div className="grid gap-[2px] sm:gap-1" style={{ gridTemplateColumns: "22px repeat(10, 1fr)" }}>
                        <div />
                        {Array.from({ length: 10 }).map((_, c) => (
                            <div key={`h-${c}`} className="text-center text-xs sm:text-sm font-bold text-cyber">{byIdx[c]?.home_digit ?? "?"}</div>
                        ))}
                        {Array.from({ length: 10 }).map((_, r) => (
                            <Fragment key={`row-${r}`}>
                                <div className="text-center text-xs sm:text-sm font-bold text-heat flex items-center justify-center">{byIdx[r * 10]?.away_digit ?? "?"}</div>
                                {Array.from({ length: 10 }).map((_, c) => {
                                    const idx = r * 10 + c;
                                    const box = byIdx[idx];
                                    const wins = winnersByIdx[idx];
                                    const isLive = data.live_box_index === idx;
                                    const tint = heatmapOn ? (TRANCHE_STYLE[box?.tranche] || "") : "";
                                    const mine = box?.participant_id === meId;
                                    // iter87.606 (user-requested) — a box the player owns gets a
                                    // blue pulsing border + blue initials. The live-score box and
                                    // any checkpoint/final winner already have their own border
                                    // treatment, which takes priority over the blue border (but the
                                    // initials stay blue either way, per the user's own framing).
                                    const winRing = wins?.includes("final")
                                        ? "border-amber-300 shadow-[0_0_8px_-1px_rgba(252,211,77,0.85)]"
                                        : wins?.length ? "border-cyan-400"
                                        : (mine && !isLive) ? "border-blue-400 shadow-[0_0_8px_-1px_rgba(96,165,250,0.85)] animate-pulse"
                                        : "border-zinc-700";
                                    return (
                                        <button
                                            key={idx}
                                            type="button"
                                            data-testid={`game-progress-box-${idx}`}
                                            data-box-tranche={box?.tranche || ""}
                                            // iter87.599 post-test fix (testing_agent HIGH finding)
                                            // — on touch devices the browser fires a compatibility
                                            // mouseenter right before click, so a toggle-on-click
                                            // immediately undid the hover-set. onPointerEnter/Leave
                                            // now only drive hover for an actual mouse; click always
                                            // just SETS the index (no toggle), which works for both
                                            // a mouse click and a touch tap.
                                            onPointerEnter={(e) => { if (e.pointerType === "mouse") setHoverIdx(idx); }}
                                            onPointerLeave={(e) => { if (e.pointerType === "mouse") setHoverIdx((v) => (v === idx ? null : v)); }}
                                            onClick={() => setHoverIdx(idx)}
                                            className={`relative aspect-square w-full rounded-sm border-2 text-[10px] sm:text-xs font-bold flex items-center justify-center transition ${tint} ${winRing} ${mine ? "text-blue-400" : "text-zinc-300"}`}
                                        >
                                            {box?.initials || ""}
                                            {isLive && <span className="absolute inset-0 rounded-sm ring-2 ring-white animate-pulse" />}
                                        </button>
                                    );
                                })}
                            </Fragment>
                        ))}
                    </div>
                </div>
                {/* iter87.605 (user-requested) — the heatmap key gets its
                    own visually-separated section (border + more top
                    spacing) under a small "Token Predictor" header,
                    instead of running straight into the grid's bottom
                    edge like it used to. */}
                <div className="mt-4 pt-3 border-t border-white/10">
                    <div className="flex items-center justify-between gap-2 mb-1.5">
                        <div className="text-left text-[10px] font-bold uppercase tracking-widest text-zinc-400" data-testid="game-progress-heatmap-title">
                            Token Predictor
                        </div>
                        {/* iter87.606 (user-requested) — starts OFF; the
                            board reads as the plain locked grid until
                            the player flips this on. */}
                        <label className="flex items-center gap-1.5 shrink-0" data-testid="game-progress-heatmap-toggle">
                            <span className="text-[9px] uppercase tracking-wide text-zinc-500">{heatmapOn ? "On" : "Off"}</span>
                            <Switch
                                checked={heatmapOn}
                                onCheckedChange={setHeatmapOn}
                                data-testid="game-progress-heatmap-switch"
                            />
                        </label>
                    </div>
                    <div className="flex flex-wrap items-center justify-start gap-x-3 gap-y-1 text-[9px] uppercase tracking-wide text-zinc-500">
                        {Object.entries(TRANCHE_LABEL).map(([k, label]) => (
                            <span key={k} className="flex items-center gap-1">
                                <span className={`w-2 h-2 rounded-sm ${TRANCHE_STYLE[k]} border border-white/20`} /> {label}
                            </span>
                        ))}
                    </div>
                    <p className="text-left text-[9px] text-zinc-500 mt-1.5" data-testid="game-progress-heatmap-disclaimer">
                        Heatmap is a rough guide based on the current score — not a guarantee.
                    </p>
                </div>
            </div>

            {data.checkpoints && (
                <div className="rounded-lg border border-white/10 bg-ink p-3" data-testid="game-progress-checkpoints-card">
                    <div className="text-[10px] uppercase tracking-wide text-zinc-500 mb-2 flex items-center gap-1.5">
                        <Flame className="w-3 h-3" /> Checkpoint Winners
                    </div>
                    <div className="space-y-1.5">
                        {Object.entries(data.checkpoints).map(([cp, r]) => (
                            <div key={cp} className="flex items-start justify-between gap-2 text-sm" data-testid={`game-progress-checkpoint-${cp}`}>
                                <span className="uppercase text-[11px] font-bold text-zinc-400 shrink-0 pt-0.5">{CHECKPOINT_LABELS[cp] || cp.toUpperCase()}</span>
                                {!r.ready ? (
                                    <span className="text-zinc-400 text-xs">Pending</span>
                                ) : (
                                    <span className={`text-xs font-bold text-right ${r.participant_id === meId ? "text-volt" : "text-zinc-300"}`}>
                                        {ls?.away_name || "Away"} {r.away_score} – {ls?.home_name || "Home"} {r.home_score} → {r.username ? `${r.username}${r.participant_id === meId ? " (You!)" : ""}` : "unclaimed box"}
                                    </span>
                                )}
                            </div>
                        ))}
                    </div>
                </div>
            )}
        </div>
    );
}
